import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAgentContext } from '@Providers';

import { screenSize } from '../../Themes/sizes';

const DEFAULT_PREVIOUS_PATH = '/monitor';
const isOnAgentRoute = (pathname: string): boolean => pathname === '/agent' || pathname.startsWith('/agent/');

/**
 * Mirrors the 3 effects from legacy GlobalWrapper.tsx:
 *  A) state → URL: when fullscreen + open, push /agent/{id}
 *  B) browser back/forward away from /agent/* → exit fullscreen
 *  C) close/minimize from /agent/* → restore previousPath
 */
const useAgentRouteSync = (): void => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeChatId, isOpen, mode, setMode, setIsOpen } = useAgentContext();
  const isFullscreen = mode === 'fullscreen';

  const previousPathRef = useRef<string>(DEFAULT_PREVIOUS_PATH);
  const isProgrammaticNavRef = useRef(false);
  const wasFullscreenOnAgentRef = useRef(false);

  // Effect A: state → URL
  useEffect(() => {
    if (!isFullscreen || !isOpen) return;
    const target = activeChatId ? `/agent/${activeChatId}` : '/agent';
    if (!isOnAgentRoute(location.pathname)) {
      previousPathRef.current = location.pathname + location.search;
    }
    isProgrammaticNavRef.current = true;
    wasFullscreenOnAgentRef.current = true;
    if (location.pathname !== target) {
      navigate(target);
    }
    // navigate/location stable, omitted intentionally to mirror legacy.
  }, [isFullscreen, isOpen, activeChatId]);

  // Effect B: browser back/forward away from /agent/* → exit fullscreen
  useEffect(() => {
    if (isProgrammaticNavRef.current) {
      isProgrammaticNavRef.current = false;
      return;
    }
    if (!isOnAgentRoute(location.pathname) && isFullscreen) {
      // Wide screens: demote to the docked sidebar. Phones: the sidebar would overflow,
      // so close the agent entirely so navigating away dismisses the fullscreen overlay.
      if (window.innerWidth >= screenSize.tablet) {
        setMode('sidebar');
      } else {
        setIsOpen(false);
      }
    }
    // isFullscreen omitted to avoid circular loop, mirrors legacy.
  }, [location.pathname]);

  // Effect C: close/minimize from /agent/* → restore previousPath
  useEffect(() => {
    if (!wasFullscreenOnAgentRef.current) return;
    if (!isOnAgentRoute(location.pathname)) return;
    if (isFullscreen && isOpen) return;
    wasFullscreenOnAgentRef.current = false;
    isProgrammaticNavRef.current = true;
    navigate(previousPathRef.current);
    // location/navigate stable, omitted intentionally to mirror legacy.
  }, [isFullscreen, isOpen]);
};

export default useAgentRouteSync;
